import { z } from 'zod';

// ─── Branded ID ────────────────────────────────────────────────────────────

export const OfferVersionIdSchema = z.number().int().brand<'OfferVersionId'>();
export type OfferVersionId = z.infer<typeof OfferVersionIdSchema>;

// ─── Version status enum ───────────────────────────────────────────────────

export const OFFER_VERSION_STATUSES = ['PUBLISHED', 'SUPERSEDED', 'ACCEPTED'] as const;
export const OfferVersionStatusSchema = z.enum(OFFER_VERSION_STATUSES);
export type OfferVersionStatus = z.infer<typeof OfferVersionStatusSchema>;

// ─── Snapshot sub-schemas ──────────────────────────────────────────────────

export const OfferSnapshotUnitDateSchema = z.object({
  from: z.string(),
  to: z.string(),
  periodGranularity: z.string().nullable().optional(),
});
export type OfferSnapshotUnitDate = z.infer<typeof OfferSnapshotUnitDateSchema>;

export const OfferSnapshotUnitPriceSchema = z.object({
  currency: z.string(),
  clientPriceNet: z.number().nullable().optional(),
  discountPercent: z.number().nullable().optional(),
});
export type OfferSnapshotUnitPrice = z.infer<typeof OfferSnapshotUnitPriceSchema>;

export const OfferSnapshotMediaUnitDetailsSchema = z
  .object({
    grwId: z.string().nullable().default(null),
    supplierNumber: z.string().nullable().default(null),
    supplier: z.string().nullable().default(null),
    mediaType: z.string().nullable().default(null),
    formatCode: z.string().nullable().default(null),
    format: z.string().nullable().default(null),
    region: z.string().nullable().default(null),
    county: z.string().nullable().default(null),
    agglomeration: z.string().nullable().default(null),
    city: z.string().nullable().default(null),
    address: z.string().nullable().default(null),
    widthM: z.number().nullable().default(null),
    heightM: z.number().nullable().default(null),
    dimensions: z.string().nullable().default(null),
    dimensionNotes: z.string().nullable().default(null),
    audienceEstimate: z.number().nullable().default(null),
    notes: z.string().nullable().default(null),
    hasPhoto: z.boolean().default(false),
  })
  .catchall(z.unknown());
export type OfferSnapshotMediaUnitDetails = z.infer<typeof OfferSnapshotMediaUnitDetailsSchema>;

export const OfferSnapshotUnitSchema = z.object({
  id: z.number(),
  mediaUnitId: z.number(),
  mediaUnitDetails: OfferSnapshotMediaUnitDetailsSchema,
  dates: z.array(OfferSnapshotUnitDateSchema),
  prices: z.array(OfferSnapshotUnitPriceSchema),
  note: z.string().nullable().optional(),
  sortOrder: z.number().optional(),
});
export type OfferSnapshotUnit = z.infer<typeof OfferSnapshotUnitSchema>;

export const OfferSnapshotSchema = z.object({
  title: z.string(),
  status: z.string(),
  clientId: z.number(),
  clientName: z.string(),
  brandingMode: z.string(),
  priceColumnsConfig: z.array(z.unknown()),
  units: z.array(OfferSnapshotUnitSchema),
  /**
   * Offer-level campaign range at publish time, used as the client-view fallback
   * for units with no explicit reservation dates (CU-86caypx6r) — without it a
   * never-reserved unit rendered as "today–today" instead of the configured range.
   * Optional: historical snapshots published before these keys existed may omit
   * them entirely, distinct from `null` (a valid explicit value once written).
   */
  defaultDateFrom: z.string().nullable().optional(),
  defaultDateTo: z.string().nullable().optional(),
});
export type OfferSnapshot = z.infer<typeof OfferSnapshotSchema>;

// ─── Offer Version base schema (shared fields, no blob, no summary aggregates) ──

const OfferVersionDateFields = {
  publishedAt: z.coerce.date(),
  acceptedAt: z.coerce.date().nullable().optional(),
  /** Set when the client rejected this version. Does not change `status`. */
  rejectedAt: z.coerce.date().nullable().optional(),
};

const OfferVersionBaseSchema = z
  .object({
    id: OfferVersionIdSchema,
    offerId: z.number(),
    versionNumber: z.number().int(),
    status: OfferVersionStatusSchema,
    changeDescription: z.string().nullable(),
    publishedAt: z.date(),
    publishedBy: z.number(),
  })
  .extend({
    id: OfferVersionIdSchema,
    status: OfferVersionStatusSchema,
    ...OfferVersionDateFields,
  });

// ─── Offer Version summary schema (list endpoint: includes computed aggregates) ──

export const OfferVersionSchema = OfferVersionBaseSchema.extend({
  /** Number of media units in the snapshot (computed by list API from JSONB). */
  unitCount: z.number().int().nonnegative(),
  /** Sum of clientPriceNet across all units; null when no prices are set. */
  totalNetPrice: z.number().nullable(),
  /** Display name of the user who published this version; null if user was deleted. */
  publishedByName: z.string().nullable(),
});
export type OfferVersion = z.infer<typeof OfferVersionSchema>;

// ─── Offer Version with full snapshot (snapshot endpoint) ─────────────────

export const OfferVersionWithSnapshotSchema = OfferVersionBaseSchema.extend({
  snapshotData: OfferSnapshotSchema,
});
export type OfferVersionWithSnapshot = z.infer<typeof OfferVersionWithSnapshotSchema>;

// ─── Publish response (single version, no blob, no aggregates — returned after publish) ──

export const PublishVersionResponseSchema = OfferVersionBaseSchema;
export type PublishVersionResponse = z.infer<typeof PublishVersionResponseSchema>;

// ─── List response ─────────────────────────────────────────────────────────

export const OfferVersionListSchema = z.object({
  versions: z.array(OfferVersionSchema),
});
export type OfferVersionList = z.infer<typeof OfferVersionListSchema>;

// ─── Publish request ───────────────────────────────────────────────────────

export const PublishVersionBodySchema = z
  .object({
    changeDescription: z.string().max(500).optional(),
    updatedAt: z.string().datetime({ offset: true }).or(z.coerce.date()),
  })
  .strict();
export type PublishVersionBody = z.infer<typeof PublishVersionBodySchema>;
